import React, { useRef, useEffect, useState } from 'react';
import logo from './logo.svg';
import './App.css';
import Button from '@mui/material/Button';
import { ImageWithFallback } from "./components/figma/ImageWithFallback";
import { Logo } from "./components/Logo";
import PopupSelect from "./components/PopupSelect";
import ContactUs from "./components/contact_us";
import Card from "@mui/material/Card";
import './styles/globals.css';
import "./App.css";
import Badge from '@mui/material/Badge';
import {
  Check, Star, Users, MessageSquare, Brain, Vote, Shield,
  ArrowRight, Menu, Search, TrendingUp, Clock, Languages,
  Target, BarChart3, FileText, Globe, Zap
} from "lucide-react";
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Mail, MapPin, Phone } from 'lucide-react';

function Privacy() {
  const navbarRef = useRef<HTMLElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [showTOC, setShowTOC] = useState<boolean>(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleSection = (sectionId: string): void => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const scrollToSection = (sectionId: string): void => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const sections = [
    { id: 'intro', title: '1. Introduction', hasSubsections: false },
    { id: 'definitions', title: '2. Definitions', hasSubsections: false },
    { id: 'collection', title: '3. Information We Collect', hasSubsections: true },
    { id: 'usage', title: '4. How We Use Your Information', hasSubsections: true },
    { id: 'legal-basis', title: '5. Legal Basis for Processing', hasSubsections: false },
    { id: 'sharing', title: '6. How We Share Your Information', hasSubsections: true },
    { id: 'retention', title: '7. Data Retention', hasSubsections: false },
    { id: 'security', title: '8. Data Security', hasSubsections: false },
    { id: 'transfers', title: '9. International Data Transfers', hasSubsections: false },
    { id: 'rights', title: '10. Your Rights Under POPIA', hasSubsections: true },
    { id: 'cookies', title: '11. Cookies and Tracking Technologies', hasSubsections: false },
    { id: 'children', title: '12. Children\'s Privacy', hasSubsections: false },
    { id: 'ai', title: '13. AI-Powered Features', hasSubsections: false },
    { id: 'third-party', title: '14. Third-Party Links', hasSubsections: false },
    { id: 'changes', title: '15. Changes to This Privacy Statement', hasSubsections: false },
    { id: 'contact', title: '16. Contact Information', hasSubsections: false },
    { id: 'consent', title: '17. Consent', hasSubsections: false },
  ];

  return (
    <div className="min-h-screen bg-background bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav
        ref={navbarRef}
        className={`navbar fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200'
            : 'bg-white/90 backdrop-blur-sm'
        }`}
      >
        <div className="navbar-left">
          <Link to="/" className="flex items-center">
            <img src="logo_transparrent.png" alt="Logo" className="logo" />
            <span className="brand">Konsensus</span>
          </Link>
        </div>
      </nav>

      {/* Header */}
      <header className="bg-white shadow-sm sticky top-20 z-50" style={{marginTop:"70px"}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                  <Link
                    to="/"
                    className="inline-flex items-center text-primary hover:text-primary/80 mb-8 transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Home
                  </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Privacy Statement</h1>
              <p className="text-sm text-slate-600 mt-1">Konsensus Platform - POPIA Compliant</p>
            </div>
            <button
              onClick={() => setShowTOC(!showTOC)}
              className="lg:hidden px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              {showTOC ? 'Hide' : 'Show'} Contents
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Table of Contents - Sidebar */}
          <aside className={`lg:col-span-1 ${showTOC ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-64">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Contents</h2>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className="block w-full text-left text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded transition"
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3 space-y-6">

            {/* Section 1: Introduction */}
            <section id="intro" className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. INTRODUCTION</h2>
              <p className="text-slate-700 mb-4">
                Welcome to Konsensus ("we", "us", "our"). We are committed to protecting your privacy and
                ensuring the security of your personal information in accordance with the Protection of Personal
                Information Act, 2013 (POPIA) and other applicable data protection legislation.
              </p>
              <p className="text-slate-700 mb-6">
                This Privacy Statement explains how we collect, use, store, share, and protect your personal
                information when you use the Konsensus platform—our technology-powered collective
                decision-making solution for voluntary associations.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-semibold text-slate-900 mb-3">Company Details</h3>
                <ul className="space-y-2 text-slate-700">
                  <li><span className="font-medium">Legal Name:</span> NeM Systems (Pty) Ltd</li>
                  <li><span className="font-medium">Registration Number:</span> 2024 / 446663 / 07</li>
                  <li className="flex items-start">
                    <MapPin className="w-4 h-4 mt-1 mr-2 flex-shrink-0" />
                    <span><span className="font-medium">Physical Address:</span> 53 Blue Valley Avenue, Hout Bay, 7806, South Africa</span>
                  </li>
                  <li className="flex items-center">
                    <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span><span className="font-medium">Email:</span>  dpo@konsensus.tech</span>
                  </li>
                  <li><span className="font-medium">Information Officer:</span> Deon Botha</li>
                </ul>
              </div>
            </section>

            {/* Section 2: Definitions */}
            <section id="definitions" className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. DEFINITIONS</h2>
              <p className="text-slate-700 mb-4">For purposes of this Privacy Statement:</p>
              <dl className="space-y-4">
                <div>
                  <dt className="font-semibold text-slate-900">"Personal Information"</dt>
                  <dd className="text-slate-700 mt-1">means information relating to an identifiable, living, natural person, and where applicable, an identifiable, existing juristic person.</dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900">"Processing"</dt>
                  <dd className="text-slate-700 mt-1">means any operation or activity concerning personal information, including collection, storage, use, modification, dissemination, or deletion.</dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900">"Platform"</dt>
                  <dd className="text-slate-700 mt-1">means the Konsensus technology solution accessible via web and mobile applications.</dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900">"User"</dt>
                  <dd className="text-slate-700 mt-1">means any individual who accesses or uses the Platform.</dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900">"Association"</dt>
                  <dd className="text-slate-700 mt-1">means the voluntary association (sectional title scheme, school, sports club, community organization, etc.) that subscribes to our services.</dd>
                </div>
              </dl>
            </section>

            {/* Section 3: Information We Collect */}
            <section id="collection" className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. INFORMATION WE COLLECT</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">3.1 Information You Provide Directly</h3>
                  <p className="text-slate-700 mb-3">When you register and use Konsensus, we collect:</p>
                  <ul className="space-y-2 text-slate-700">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span><span className="font-medium">Account Information:</span> Name, surname, email address, phone number, username, password</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span><span className="font-medium">Profile Information:</span> Role within your association, preferences, profile picture (optional)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span><span className="font-medium">Association Information:</span> Name and details of the association you represent or belong to</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span><span className="font-medium">Content Data:</span> Discussion posts, comments, poll responses, voting records, opinions, suggestions, and other contributions</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span><span className="font-medium">Communication Data:</span> Messages sent through the Platform, feedback, and support inquiries</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">3.2 Information Collected Automatically</h3>
                  <p className="text-slate-700 mb-3">When you use the Platform, we automatically collect:</p>
                  <ul className="space-y-2 text-slate-700">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span><span className="font-medium">Usage Data:</span> Features accessed, pages viewed, time spent on Platform, interaction patterns</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span><span className="font-medium">Device Information:</span> Device type, operating system, browser type, IP address, unique device identifiers</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span><span className="font-medium">Log Data:</span> Access times, error logs, system activity</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span><span className="font-medium">Analytics Data:</span> Aggregated usage statistics and engagement metrics</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">3.3 Information from Third Parties</h3>
                  <p className="text-slate-700 mb-3">We may receive information from:</p>
                  <ul className="space-y-2 text-slate-700">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span><span className="font-medium">Association Administrators:</span> Member lists, roles, and contact information</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span><span className="font-medium">Authentication Services:</span> If you use third-party login services (e.g., Google, Microsoft)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span><span className="font-medium">Payment Processors:</span> Transaction records for subscription payments</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 4: How We Use Your Information */}
            <section id="usage" className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. HOW WE USE YOUR INFORMATION</h2>
              <p className="text-slate-700 mb-6">We process your personal information for the following lawful purposes:</p>

              <div className="space-y-6">
                <div className="border-l-4 border-blue-600 pl-4">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">4.1 Service Delivery (Contract Performance)</h3>
                  <ul className="space-y-2 text-slate-700">
                    <li>• Providing access to the Konsensus Platform</li>
                    <li>• Facilitating discussions, voting, and consensus-building activities</li>
                    <li>• Enabling AI-powered features including sentiment analysis, bias detection, topic structuring, and compromise identification</li>
                    <li>• Managing your account and authentication</li>
                    <li>• Providing customer support and responding to inquiries</li>
                  </ul>
                </div>

                <div className="border-l-4 border-green-600 pl-4">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">4.2 Platform Improvement (Legitimate Interest)</h3>
                  <ul className="space-y-2 text-slate-700">
                    <li>• Analyzing usage patterns to improve features and user experience</li>
                    <li>• Developing new functionalities and services</li>
                    <li>• Conducting research and analytics on collective decision-making processes</li>
                    <li>• Testing and optimizing AI algorithms for discussion facilitation</li>
                  </ul>
                </div>

                <div className="border-l-4 border-purple-600 pl-4">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">4.3 Communication (Legitimate Interest / Consent)</h3>
                  <ul className="space-y-2 text-slate-700">
                    <li>• Sending service-related notifications and updates</li>
                    <li>• Providing information about discussions requiring your input</li>
                    <li>• Sharing Platform updates and new features</li>
                    <li>• Marketing communications (only with your explicit consent, which you may withdraw at any time)</li>
                  </ul>
                </div>

                <div className="border-l-4 border-red-600 pl-4">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">4.4 Legal Compliance and Security (Legal Obligation / Legitimate Interest)</h3>
                  <ul className="space-y-2 text-slate-700">
                    <li>• Complying with POPIA and other applicable laws</li>
                    <li>• Preventing fraud, abuse, and security incidents</li>
                    <li>• Maintaining records for dispute resolution</li>
                    <li>• Enforcing our Terms of Service</li>
                  </ul>
                </div>

                <div className="border-l-4 border-amber-600 pl-4">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">4.5 Association Governance (Legitimate Interest)</h3>
                  <ul className="space-y-2 text-slate-700">
                    <li>• Maintaining institutional memory and searchable archives</li>
                    <li>• Creating audit trails for decision-making processes</li>
                    <li>• Ensuring regulatory compliance for associations</li>
                    <li>• Facilitating knowledge transfer and succession planning</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 5: Legal Basis */}
            <section id="legal-basis" className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. LEGAL BASIS FOR PROCESSING</h2>
              <p className="text-slate-700 mb-4">
                We process your personal information based on one or more of the following lawful grounds under POPIA:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">1. Consent</h4>
                  <p className="text-sm text-slate-700">You have given explicit permission</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">2. Contract</h4>
                  <p className="text-sm text-slate-700">Processing is necessary to perform our contract with you or your association</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">3. Legal Obligation</h4>
                  <p className="text-sm text-slate-700">We must process your information to comply with the law</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">4. Legitimate Interest</h4>
                  <p className="text-sm text-slate-700">Processing is necessary for our legitimate interests or those of your association, provided this does not override your rights</p>
                </div>
              </div>
            </section>

            {/* Section 6: How We Share Your Information */}
            <section id="sharing" className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. HOW WE SHARE YOUR INFORMATION</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">6.1 Within Your Association</h3>
                  <p className="text-slate-700">
                    Your contributions, voting records, and participation data are visible to other members of your
                    association according to the visibility settings configured by your association's administrators.
                    Some features allow anonymous participation to protect sensitive feedback.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">6.2 Service Providers</h3>
                  <p className="text-slate-700 mb-3">We share information with trusted third-party service providers who assist us in:</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="bg-slate-50 rounded px-3 py-2 text-sm text-slate-700">Cloud hosting</div>
                    <div className="bg-slate-50 rounded px-3 py-2 text-sm text-slate-700">Payment processing</div>
                    <div className="bg-slate-50 rounded px-3 py-2 text-sm text-slate-700">Email services</div>
                    <div className="bg-slate-50 rounded px-3 py-2 text-sm text-slate-700">Analytics</div>
                    <div className="bg-slate-50 rounded px-3 py-2 text-sm text-slate-700">AI/ML services</div>
                    <div className="bg-slate-50 rounded px-3 py-2 text-sm text-slate-700">Customer support</div>
                  </div>
                  <p className="text-sm text-slate-600 mt-3 italic">
                    All service providers are contractually bound to protect your information and use it only for specified purposes.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">6.3 Legal Requirements</h3>
                  <p className="text-slate-700 mb-2">We may disclose your information when required by law, court order, or regulatory authority, or to:</p>
                  <ul className="space-y-1 text-slate-700">
                    <li>• Protect our legal rights</li>
                    <li>• Prevent fraud or illegal activities</li>
                    <li>• Protect the safety of users or the public</li>
                    <li>• Respond to lawful requests from authorities</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">6.4 Business Transfers</h3>
                  <p className="text-slate-700">
                    In the event of a merger, acquisition, or sale of assets, your information may be transferred to
                    the acquiring entity, subject to the same privacy protections.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">6.5 Aggregated and Anonymized Data</h3>
                  <p className="text-slate-700">
                    We may share aggregated, anonymized data that cannot identify you personally for research,
                    marketing, or analytics purposes.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 7: Data Retention */}
            <section id="retention" className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. DATA RETENTION</h2>
              <p className="text-slate-700 mb-4">We retain your personal information for as long as:</p>
              <ul className="space-y-2 text-slate-700 mb-6">
                <li>• Your account remains active</li>
                <li>• Required to provide services to your association</li>
                <li>• Necessary for legal, regulatory, or contractual obligations</li>
                <li>• Required for legitimate business purposes (e.g., institutional memory, dispute resolution)</li>
              </ul>

              <div className="bg-slate-50 rounded-lg p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Specific Retention Periods:</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="w-40 flex-shrink-0 font-medium text-slate-900">Account Data:</div>
                    <div className="text-slate-700">Duration of account + 7 years after closure (for audit and legal purposes)</div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-40 flex-shrink-0 font-medium text-slate-900">Decision Records:</div>
                    <div className="text-slate-700">Indefinitely or as determined by your association's governance requirements</div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-40 flex-shrink-0 font-medium text-slate-900">Analytics Data:</div>
                    <div className="text-slate-700">Aggregated form retained indefinitely; individual data retained for 3 years</div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-40 flex-shrink-0 font-medium text-slate-900">Support Comms:</div>
                    <div className="text-slate-700">5 years</div>
                  </div>
                </div>
              </div>

              <p className="text-slate-700 mt-4 text-sm">
                You may request deletion of your personal information, subject to our legal and contractual
                obligations. Some data may need to be retained for historical record-keeping required by your
                association's governance framework.
              </p>
            </section>

            {/* Section 8: Data Security */}
            <section id="security" className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. DATA SECURITY</h2>
              <p className="text-slate-700 mb-6">
                We implement appropriate technical and organizational measures to protect your information
                against unauthorized access, alteration, disclosure, or destruction, including:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">🔒 Encryption</h4>
                  <p className="text-sm text-slate-700">Data encrypted in transit (TLS/SSL) and at rest</p>
                </div>
                <div className="border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">🔑 Access Controls</h4>
                  <p className="text-sm text-slate-700">Role-based access restrictions and authentication protocols</p>
                </div>
                <div className="border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">👁️ Security Monitoring</h4>
                  <p className="text-sm text-slate-700">Continuous monitoring for suspicious activity</p>
                </div>
                <div className="border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">🔍 Regular Audits</h4>
                  <p className="text-sm text-slate-700">Periodic security assessments and penetration testing</p>
                </div>
                <div className="border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">👥 Staff Training</h4>
                  <p className="text-sm text-slate-700">Regular privacy and security training for personnel</p>
                </div>
                <div className="border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">💾 Backup Systems</h4>
                  <p className="text-sm text-slate-700">Secure, encrypted backups with disaster recovery procedures</p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-slate-700">
                  ⚠️ While we take reasonable measures to protect your information, no system is completely
                  secure. We cannot guarantee absolute security of data transmitted over the internet.
                </p>
              </div>
            </section>

            {/* Section 9: International Data Transfers */}
            <section id="transfers" className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. INTERNATIONAL DATA TRANSFERS</h2>
              <p className="text-slate-700 mb-4">
                Your information may be processed and stored in countries outside South Africa, including
                jurisdictions that may not provide the same level of data protection. When we transfer data
                internationally, we ensure appropriate safeguards are in place, such as:
              </p>
              <ul className="space-y-2 text-slate-700 mb-4">
                <li>• Standard contractual clauses approved by data protection authorities</li>
                <li>• Adequacy decisions recognizing equivalent protection</li>
                <li>• Binding corporate rules for intra-group transfers</li>
              </ul>
              <p className="text-slate-700">
                By using our Platform, you consent to international transfers as described in this Privacy Statement.
              </p>
            </section>

            {/* Section 10: Your Rights Under POPIA */}
            <section id="rights" className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">10. YOUR RIGHTS UNDER POPIA</h2>
              <p className="text-slate-700 mb-6">You have the following rights regarding your personal information:</p>

              <div className="space-y-4">
                <div className="border-l-4 border-blue-600 pl-4">
                  <h4 className="font-semibold text-slate-900">10.1 Right of Access</h4>
                  <p className="text-slate-700 text-sm mt-1">Request confirmation of whether we hold your personal information and obtain a copy of such information.</p>
                </div>
                <div className="border-l-4 border-green-600 pl-4">
                  <h4 className="font-semibold text-slate-900">10.2 Right to Correction</h4>
                  <p className="text-slate-700 text-sm mt-1">Request correction, updating, or completion of inaccurate or incomplete information.</p>
                </div>
                <div className="border-l-4 border-red-600 pl-4">
                  <h4 className="font-semibold text-slate-900">10.3 Right to Deletion</h4>
                  <p className="text-slate-700 text-sm mt-1">Request deletion of your personal information, subject to legal and legitimate retention requirements.</p>
                </div>
                <div className="border-l-4 border-purple-600 pl-4">
                  <h4 className="font-semibold text-slate-900">10.4 Right to Object</h4>
                  <p className="text-slate-700 text-sm mt-1">Object to processing of your information for direct marketing or based on legitimate interests.</p>
                </div>
                <div className="border-l-4 border-amber-600 pl-4">
                  <h4 className="font-semibold text-slate-900">10.5 Right to Restriction</h4>
                  <p className="text-slate-700 text-sm mt-1">Request restriction of processing in certain circumstances.</p>
                </div>
                <div className="border-l-4 border-cyan-600 pl-4">
                  <h4 className="font-semibold text-slate-900">10.6 Right to Data Portability</h4>
                  <p className="text-slate-700 text-sm mt-1">Receive your personal information in a structured, commonly used format and transmit it to another controller.</p>
                </div>
                <div className="border-l-4 border-pink-600 pl-4">
                  <h4 className="font-semibold text-slate-900">10.7 Right to Withdraw Consent</h4>
                  <p className="text-slate-700 text-sm mt-1">Withdraw consent for processing at any time (where processing is based on consent).</p>
                </div>
                <div className="border-l-4 border-slate-600 pl-4">
                  <h4 className="font-semibold text-slate-900">10.8 Right to Lodge a Complaint</h4>
                  <p className="text-slate-700 text-sm mt-1">Lodge a complaint with the Information Regulator if you believe your rights have been violated.</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <p className="text-slate-700">
                  <span className="font-semibold">To exercise your rights,</span> contact our Information Officer at: <a href="mailto:dpo@konsensus.tech" className="text-blue-600 hover:underline">dpo@konsensus.tech</a>
                </p>
                <p className="text-slate-700 text-sm mt-2">We will respond to your request within 30 days or as required by law.</p>
              </div>
            </section>

            {/* Section 11: Cookies */}
            <section id="cookies" className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">11. COOKIES AND TRACKING TECHNOLOGIES</h2>
              <p className="text-slate-700 mb-4">We use cookies and similar technologies to:</p>
              <ul className="space-y-2 text-slate-700 mb-4">
                <li>• Maintain your session and preferences</li>
                <li>• Analyze Platform usage and performance</li>
                <li>• Provide personalized features</li>
                <li>• Ensure security and prevent fraud</li>
              </ul>
              <p className="text-slate-700">
                You can manage cookie preferences through your browser settings. Disabling cookies may
                affect Platform functionality.
              </p>
            </section>

            {/* Section 12: Children's Privacy */}
            <section id="children" className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">12. CHILDREN'S PRIVACY</h2>
              <p className="text-slate-700 mb-4">
                While Konsensus serves associations including schools, the Platform is not intended for direct
                use by children under 18 without parental or guardian consent. If you are under 18, please
                ensure your parent or guardian reviews this Privacy Statement and consents to your use of the
                Platform.
              </p>
              <p className="text-slate-700">
                If we become aware that we have collected personal information from a child under 18 without
                proper consent, we will take steps to delete such information.
              </p>
            </section>

            {/* Section 13: AI-Powered Features */}
            <section id="ai" className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">13. AI-POWERED FEATURES</h2>
              <p className="text-slate-700 mb-4">Our Platform uses artificial intelligence and machine learning for:</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-1">Sentiment Analysis</h4>
                  <p className="text-sm text-slate-700">Analyzing tone and emotional content of discussions</p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-1">Bias Detection</h4>
                  <p className="text-sm text-slate-700">Identifying potentially exclusionary or biased language</p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-1">Topic Structuring</h4>
                  <p className="text-sm text-slate-700">Organizing discussions into themes</p>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-1">Compromise Identification</h4>
                  <p className="text-sm text-slate-700">Suggesting solutions based on stated preferences</p>
                </div>
                <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg p-4 md:col-span-2">
                  <h4 className="font-semibold text-slate-900 mb-1">Impact Assessment</h4>
                  <p className="text-sm text-slate-700">Predictive modeling of decision impacts</p>
                </div>
              </div>

              <p className="text-slate-700">
                These AI features process your discussion content and contributions. You can opt out of certain
                AI features through your account settings, though this may limit Platform functionality.
              </p>
            </section>

            {/* Section 14: Third-Party Links */}
            <section id="third-party" className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">14. THIRD-PARTY LINKS</h2>
              <p className="text-slate-700">
                The Platform may contain links to third-party websites or services. We are not responsible for
                the privacy practices of these external sites. We encourage you to review their privacy policies
                before providing any personal information.
              </p>
            </section>

            {/* Section 15: Changes */}
            <section id="changes" className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">15. CHANGES TO THIS PRIVACY STATEMENT</h2>
              <p className="text-slate-700 mb-4">
                We may update this Privacy Statement periodically to reflect changes in our practices,
                technology, legal requirements, or business operations. We will notify you of material changes by:
              </p>
              <ul className="space-y-2 text-slate-700 mb-4">
                <li>• Posting the updated statement on the Platform with a new "Last Updated" date</li>
                <li>• Sending email notification to registered users</li>
                <li>• Displaying a prominent notice on the Platform</li>
              </ul>
              <p className="text-slate-700">
                Your continued use of the Platform after changes become effective constitutes acceptance of
                the updated Privacy Statement.
              </p>
            </section>

            {/* Section 16: Contact Information */}
            <section id="contact" className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">16. CONTACT INFORMATION</h2>
              <p className="text-slate-700 mb-6">
                For questions, concerns, or requests regarding this Privacy Statement or our data practices:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Information Officer</h3>
                  <div className="space-y-2 text-slate-700">
                    <p><span className="font-medium">Name:</span> Deon Botha</p>
                    <p className="flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      <a href="mailto:dpo@konsensus.tech" className="text-blue-600 hover:underline">dpo@konsensus.tech</a>
                    </p>
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Information Regulator (South Africa)</h3>
                  <p className="text-sm text-slate-700 mb-3">If you wish to lodge a complaint:</p>
                  <div className="space-y-2 text-slate-700 text-sm">
                    <p>JD House, 27 Stiemens Street<br/>Braamfontein, Johannesburg, 2001</p>
                    <p>P.O Box 31533<br/>Braamfontein, Johannesburg, 2017</p>
                    <p className="flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      <a href="mailto:inforeg@justice.gov.za" className="text-blue-600 hover:underline">inforeg@justice.gov.za</a>
                    </p>
                    <p>
                      <a href="https://www.justice.gov.za/inforeg/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.justice.gov.za/inforeg/</a>
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 17: Consent */}
            <section id="consent" className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">17. CONSENT</h2>
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-300 rounded-lg p-6">
                <p className="text-slate-700 mb-4">
                  By creating an account and using the Konsensus Platform, you acknowledge that you have
                  read, understood, and agree to this Privacy Statement and consent to the collection, use, and
                  disclosure of your personal information as described herein.
                </p>
                <p className="text-slate-700 text-sm">
                  For processing activities requiring explicit consent (such as marketing communications), we will
                  obtain your separate, specific consent at the time of collection.
                </p>
              </div>
            </section>

            {/* Footer */}
            <footer className="bg-slate-100 rounded-lg p-6 text-center text-sm text-slate-600">
              <p>Last Updated: November 2, 2025</p>
              <p className="mt-2">© 2025 NeM Systems (Pty) Ltd. All rights reserved.</p>
            </footer>

          </main>
        </div>
      </div>
    </div>
  );
}

export default Privacy;